
/**
 * What a notification is about, which decides how it looks. Same set and
 * colours as the mobile app, so a bar manager sees the same thing on both.
 */
export interface NotificationKind {
  key: 'order' | 'orderCancelled' | 'payment' | 'paymentFailed' | 'refund'
     | 'lowStock' | 'outOfStock' | 'account' | 'other';
  label: string;
  icon: string;   // Material icon name
  color: string;
}

export const NOTIFICATION_KINDS: Record<NotificationKind['key'], NotificationKind> = {
  order:          { key: 'order',          label: 'Order',           icon: 'receipt_long',          color: '#1E66D0' },
  orderCancelled: { key: 'orderCancelled', label: 'Order cancelled', icon: 'cancel',                color: '#B3261E' },
  payment:        { key: 'payment',        label: 'Payment',         icon: 'payments',              color: '#1B873F' },
  paymentFailed:  { key: 'paymentFailed',  label: 'Payment failed',  icon: 'money_off',             color: '#B3261E' },
  refund:         { key: 'refund',         label: 'Refund',          icon: 'currency_exchange',     color: '#7B3FC4' },
  lowStock:       { key: 'lowStock',       label: 'Low stock',       icon: 'inventory_2',           color: '#B26A00' },
  outOfStock:     { key: 'outOfStock',     label: 'Out of stock',    icon: 'remove_shopping_cart',  color: '#B3261E' },
  account:        { key: 'account',        label: 'Account',         icon: 'person',                color: '#00796B' },
  other:          { key: 'other',          label: 'Notification',    icon: 'notifications',         color: '#5F6368' },
};

/** One notification as the pages use it. */
export interface Notification {
  id: number;
  title: string;
  message: string;
  reason: string | null;
  sentAt: Date | null;
  /**
   * The service's `delivered` flag. Despite the name it is the read flag: it
   * is what mark-as-read sets and what the unread count counts.
   */
  isRead: boolean;
  kind: NotificationKind;
}

/** The notification service's page of results. */
export interface NotificationPage {
  notificationResponseList: ServerNotification[];
  pageInfoDTO?: { totalElements: number; totalPages: number; currentPage: number };
}

export interface ServerNotification {
  id: number;
  title?: string | null;
  message?: string | null;
  notificationReason?: string | null;
  notificationDateTime?: string | null;
  notificationDateTimeUtc?: string | null;
  delivered?: boolean;
}

export function notificationKind(title: string | null | undefined,
                                 reason: string | null | undefined): NotificationKind {
  const t = (title ?? '').toLowerCase();
  const r = (reason ?? '').toLowerCase();
  if (t.includes('out of stock') || t.includes('out-of-stock')) return NOTIFICATION_KINDS.outOfStock;
  if (t.includes('low stock') || r === 'inventory') return NOTIFICATION_KINDS.lowStock;
  if (r === 'refund' || t.includes('refund')) return NOTIFICATION_KINDS.refund;
  if (r === 'payment' || t.includes('payment')) {
    return (t.includes('fail') || t.includes('cancel'))
      ? NOTIFICATION_KINDS.paymentFailed : NOTIFICATION_KINDS.payment;
  }
  if (r === 'order' || t.includes('order')) {
    return t.includes('cancel') ? NOTIFICATION_KINDS.orderCancelled : NOTIFICATION_KINDS.order;
  }
  if (r === 'account') return NOTIFICATION_KINDS.account;
  return NOTIFICATION_KINDS.other;
}

/**
 * A server timestamp as a Date. The service runs on UTC and its older field
 * carries no zone ("2026-10-26T22:02:00"), which a browser reads as local
 * time — hours off for anyone outside UTC. Zone-less strings are taken as UTC.
 */
export function parseServerTime(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const text = raw.trim();
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(text);
  // Browsers parse at most milliseconds; the server sends microseconds.
  const trimmed = text.replace(/(\.\d{3})\d+/, '$1');
  const d = new Date(hasZone ? trimmed : trimmed + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

export function fromServerNotification(n: ServerNotification): Notification {
  const kind = notificationKind(n.title, n.notificationReason);
  return {
    id: n.id,
    title: n.title && n.title.trim() ? n.title : kind.label,
    message: n.message ?? '',
    reason: n.notificationReason ?? null,
    sentAt: parseServerTime(n.notificationDateTimeUtc) ?? parseServerTime(n.notificationDateTime),
    isRead: n.delivered === true,
    kind,
  };
}

export interface DeviceToken {
  id?: number;
  userId: number;
  token: string;
  deviceType: string;
  createdAt?: string;
}

export interface DeviceTokenRequest {
  userId: number;
  token: string;
  deviceType: string;
}

export interface TestPushRequest {
  token: string;
  title: string;
  message: string;
}
